import { roleCatalogue, signersText, type TierRow } from '../lib/api'
import type { ExitRowDoc, ExitStageDoc, ExitVocabulary } from '../lib/exit'
import { Toggletip } from './kit'
import '../styles/sweep.css'

// Who has to sign a vendor out of the register, per tier, edited beside who
// signs it in.
//
// The exit rows are a section of the same approval matrix document, so they
// are drafted, saved, checked and promoted by the controls above, and a draft
// that breaks a rule is refused at promotion with the problem listed. This
// editor adds no rule of its own. It offers the roles the role table serves and
// the conditions and audiences the version list serves.

function stageKeyFrom(job: string): string {
  const key = job
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
  return /^[A-Z]/.test(key) ? key : 'STAGE_' + key
}

function rowIndex(rows: ExitRowDoc[], tier: number, noAlternate: boolean): number {
  return rows.findIndex((r) => r.tier === tier && Boolean(r.noAlternate) === noAlternate)
}

// What this editor draws as an error, counted for the tab that opens it: a
// tier with no exit row once the version has any, and a stage nobody could
// sign. A version with no exit chain at all is not one of them: exits fall
// back to the shipped chain. It counts what is drawn below and nothing else,
// so the tab's mark and the part it opens cannot disagree.
export function exitProblemCount(rows: ExitRowDoc[] | undefined, tiers: TierRow[]): number {
  const current = rows ?? []
  if (current.length === 0) return 0
  const unsigned = (row: ExitRowDoc | undefined) => (row ? row.stages.filter((s) => s.roles.length === 0).length : 0)
  return tiers.reduce((n, t) => {
    const base = current[rowIndex(current, t.tier, false)]
    const alt = t.tier === 4 ? current[rowIndex(current, t.tier, true)] : undefined
    return n + (base ? unsigned(base) : 1) + unsigned(alt)
  }, 0)
}

export function ExitChainEditor({
  rows,
  tiers,
  vocabulary,
  editable,
  onChange,
}: {
  rows: ExitRowDoc[] | undefined
  tiers: TierRow[]
  vocabulary: ExitVocabulary
  editable: boolean
  onChange: (rows: ExitRowDoc[]) => void
}) {
  const current = rows ?? []

  return (
    <section className="panel" aria-labelledby="exit-chain-heading">
      <h2 id="exit-chain-heading">Who signs a vendor out</h2>
      {/* One line for what the chain is; the rules every stage keeps are one
          press away, since an editor is read for its fields. */}
      <p className="sw-help">
        One chain per tier, signed in order by a different person at each stage{' '}
        <Toggletip label="How an offboarding chain is signed">
          Each person holds one of the roles the stage names, and nobody who started offboarding may sign it. Tier 1 can
          have a longer chain for a provider with no alternate.
        </Toggletip>
      </p>

      {current.length === 0 && (
        <div className="empty">
          <strong>This version has no offboarding chain</strong>
          Offboarding falls back to the shipped chain until a version with one is promoted
          {editable && vocabulary.defaultExit.length > 0 && (
            <p style={{ marginTop: 8 }}>
              <button type="button" onClick={() => onChange(vocabulary.defaultExit)}>
                Start from the shipped offboarding chain
              </button>
            </p>
          )}
        </div>
      )}

      {current.length > 0 &&
        tiers.map((t) => {
          const baseAt = rowIndex(current, t.tier, false)
          const altAt = rowIndex(current, t.tier, true)
          const base = baseAt >= 0 ? current[baseAt] : undefined
          const alt = altAt >= 0 ? current[altAt] : undefined
          const put = (at: number, next: ExitRowDoc) =>
            onChange(at >= 0 ? current.map((r, i) => (i === at ? next : r)) : [...current, next])
          return (
            <div key={t.tier} className="gate-card">
              <div className="gate-head">
                <strong>{t.label}</strong>
              </div>
              {base ? (
                <ExitRowEditor
                  row={base}
                  idBase={'exit-' + t.tier}
                  vocabulary={vocabulary}
                  editable={editable}
                  onChange={(next) => put(baseAt, next)}
                />
              ) : (
                <p className="field-error">No offboarding row for this tier, so this version cannot be promoted</p>
              )}

              {t.tier === 4 && (
                <>
                  <label className="check" htmlFor={'exit-' + t.tier + '-noalt'}>
                    <input
                      id={'exit-' + t.tier + '-noalt'}
                      type="checkbox"
                      disabled={!editable || !base}
                      checked={alt !== undefined}
                      onChange={(e) => {
                        if (e.target.checked && base) {
                          onChange([
                            ...current,
                            { ...base, noAlternate: true, stages: base.stages.map((s) => ({ ...s, roles: [...s.roles] })) },
                          ])
                        } else {
                          onChange(current.filter((_, i) => i !== altAt))
                        }
                      }}
                    />
                    <span>A longer chain where the provider has no alternate</span>
                  </label>
                  {alt && (
                    <ExitRowEditor
                      row={alt}
                      idBase={'exit-' + t.tier + '-alt'}
                      vocabulary={vocabulary}
                      editable={editable}
                      onChange={(next) => put(altAt, next)}
                    />
                  )}
                </>
              )}
            </div>
          )
        })}
    </section>
  )
}

function ExitRowEditor({
  row,
  idBase,
  vocabulary,
  editable,
  onChange,
}: {
  row: ExitRowDoc
  idBase: string
  vocabulary: ExitVocabulary
  editable: boolean
  onChange: (next: ExitRowDoc) => void
}) {
  const roles = roleCatalogue().filter((r) => r.code !== 'platform_admin')
  const stages = row.stages

  function putStages(next: ExitStageDoc[]) {
    onChange({ ...row, stages: next })
  }
  function putStage(i: number, patch: Partial<ExitStageDoc>) {
    putStages(stages.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  }
  function move(from: number, to: number) {
    if (to < 0 || to >= stages.length) return
    const next = [...stages]
    const a = next[from]
    const b = next[to]
    if (!a || !b) return
    next[from] = b
    next[to] = a
    putStages(next)
  }

  return (
    <div style={{ marginTop: 8 }}>
      <ol className="act-chain">
        {stages.map((s, i) => {
          const at = idBase + '-' + i
          return (
            <li key={i}>
              <div className="form-row">
                <div className="field">
                  <label htmlFor={at + '-job'}>Stage {i + 1}</label>
                  <input
                    id={at + '-job'}
                    type="text"
                    disabled={!editable}
                    value={s.job}
                    onChange={(e) => putStage(i, { job: e.target.value })}
                    onBlur={() => {
                      if (s.key === '' && s.job.trim() !== '') putStage(i, { key: stageKeyFrom(s.job) })
                    }}
                  />
                </div>
                <div className="field">
                  <label htmlFor={at + '-key'}>Key</label>
                  <input
                    id={at + '-key'}
                    type="text"
                    className="mono"
                    disabled={!editable}
                    value={s.key}
                    onChange={(e) => putStage(i, { key: e.target.value.toUpperCase() })}
                    aria-describedby={at + '-key-note'}
                  />
                  <p className="field-note" id={at + '-key-note'}>
                    Signatures are recorded against it, so keep it when renaming the job
                  </p>
                </div>
                <div className="field">
                  <label htmlFor={at + '-when'}>In force</label>
                  <select
                    id={at + '-when'}
                    disabled={!editable}
                    value={s.when ?? ''}
                    onChange={(e) => putStage(i, { when: e.target.value || undefined })}
                  >
                    <option value="">Always</option>
                    {vocabulary.exitConditions.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ flex: '1 1 auto' }}>
                  <label htmlFor={at + '-why'}>What this signature is for</label>
                  <input
                    id={at + '-why'}
                    type="text"
                    disabled={!editable}
                    value={s.why}
                    onChange={(e) => putStage(i, { why: e.target.value })}
                  />
                </div>
              </div>
              {/* Folded, because the role table is eighteen rows and a chain of
                  six stages printing all of them is a page nobody reads. The
                  summary says who signs, which is what a reviewer scans for. */}
              <details className="act-why">
                <summary>
                  Signed by{' '}
                  {s.roles.length === 0 ? 'nobody yet' : signersText(s.roles)}
                </summary>
                <div className="colgrid">
                  {roles.map((r) => {
                    const id = at + '-role-' + r.code
                    const on = s.roles.includes(r.code)
                    return (
                      <div key={r.code} className="check">
                        <input
                          id={id}
                          type="checkbox"
                          disabled={!editable}
                          checked={on}
                          onChange={() =>
                            putStage(i, { roles: on ? s.roles.filter((x) => x !== r.code) : [...s.roles, r.code] })
                          }
                        />
                        <label htmlFor={id}>{r.name}</label>
                      </div>
                    )
                  })}
                </div>
              </details>
              {s.roles.length === 0 && <p className="field-error">No role signs this stage, so it could never be signed</p>}
              {editable && (
                <div className="chiprow">
                  <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)}>
                    Move earlier
                  </button>
                  <button type="button" disabled={i === stages.length - 1} onClick={() => move(i, i + 1)}>
                    Move later
                  </button>
                  <button type="button" onClick={() => putStages(stages.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ol>
      {editable && (
        <p>
          <button type="button" onClick={() => putStages([...stages, { key: '', job: '', roles: [], why: '' }])}>
            Add a stage
          </button>
        </p>
      )}

      <fieldset className="fieldset">
        <legend>Told when offboarding closes</legend>
        <div className="colgrid">
          {vocabulary.exitAudiences.map((a) => {
            const id = idBase + '-notify-' + a.code
            const on = (row.notify ?? []).includes(a.code)
            return (
              <div key={a.code} className="check">
                <input
                  id={id}
                  type="checkbox"
                  disabled={!editable}
                  checked={on}
                  onChange={() =>
                    onChange({
                      ...row,
                      notify: on ? (row.notify ?? []).filter((x) => x !== a.code) : [...(row.notify ?? []), a.code],
                    })
                  }
                />
                <label htmlFor={id}>{a.name}</label>
              </div>
            )
          })}
        </div>
        <p className="sw-help">An in-app notice goes to everybody holding the capability for that audience</p>
      </fieldset>

      <div className="field">
        <label htmlFor={idBase + '-rowwhy'}>Why this tier signs this way</label>
        <textarea
          id={idBase + '-rowwhy'}
          rows={2}
          disabled={!editable}
          value={row.why}
          onChange={(e) => onChange({ ...row, why: e.target.value })}
        />
      </div>
    </div>
  )
}
